import React, { useEffect, useState } from 'react'
import { gql } from 'apollo-boost'
import { useQuery } from '@apollo/react-hooks'
import { useAuth0 } from '@auth0/auth0-react'
import {
  Card,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
  Chip,
  createStyles,
  makeStyles
} from '@material-ui/core'
import moment from 'moment'
import { DCS_WS_BASE_URL } from '../../env'

const useDcsOnlineWebsocket = (deviceId: string | undefined, onStatusChange?: () => void) => {
  const [online, setOnline] = useState<boolean | null>(null)
  const { getAccessTokenSilently } = useAuth0()

  useEffect(() => {
    if (!deviceId || !DCS_WS_BASE_URL) {
      setOnline(null)
      return
    }

    let cancelled = false
    let currentWs: WebSocket | null = null
    let reconnectTimeout: ReturnType<typeof setTimeout> | null = null
    let pingInterval: ReturnType<typeof setInterval> | null = null
    let hasReceivedInitial = false

    const connect = async () => {
      if (cancelled) return

      let tokenParam = ''
      try {
        const accessToken = await getAccessTokenSilently()
        tokenParam = `?token=${encodeURIComponent(accessToken)}`
      } catch {}

      if (cancelled) return

      const ws = new WebSocket(`${DCS_WS_BASE_URL}/ws/online/${deviceId}${tokenParam}`)
      currentWs = ws

      ws.onopen = () => {
        if (cancelled) { ws.close(); return }
        pingInterval = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: 'ping' }))
          }
        }, 30000)
      }

      ws.onmessage = (event: MessageEvent) => {
        if (cancelled) return
        try {
          const data = JSON.parse(event.data)
          if (typeof data?.online === 'boolean') {
            setOnline(prev => {
              if (hasReceivedInitial && prev !== data.online) {
                onStatusChange?.()
              }
              hasReceivedInitial = true
              return data.online
            })
          }
        } catch {}
      }

      ws.onerror = () => {
        if (!cancelled) setOnline(null)
      }

      ws.onclose = () => {
        if (pingInterval) {
          clearInterval(pingInterval)
          pingInterval = null
        }
        if (!cancelled) {
          reconnectTimeout = setTimeout(connect, 3000)
        }
      }
    }

    connect()

    return () => {
      cancelled = true
      if (reconnectTimeout) clearTimeout(reconnectTimeout)
      if (pingInterval) clearInterval(pingInterval)
      currentWs?.close()
    }
  }, [deviceId])

  return online
}

const V2_PRESENCE_QUERY = gql`
  query V2PresenceQuery($deviceId: String!) {
    flagshipDeviceV2(id: $deviceId) {
      ... on FlagshipDeviceV2 {
        id
        presence {
          deviceId
          online
          lastSeen
          transport
          connectedAt
          lastContact
          ssid
          ipAddress
          ethernetOnline
          ethernetIpAddress
          wifiIpAddress
          maskStatus
          firmwareVersion
          firmwareUpgradeStatus
          firmwareUpgradeProgress
        }
      }
    }
  }
`

const useStyles = makeStyles(() =>
  createStyles({
    card: {
      marginTop: 24,
      padding: 16
    },
    title: {
      fontWeight: 700,
      marginBottom: 12,
      display: 'flex',
      alignItems: 'center',
      gap: 8
    },
    chip: {
      fontWeight: 700
    }
  })
)

interface V2PresenceProps {
  deviceId: string
}

export const V2Presence = ({ deviceId }: V2PresenceProps) => {
  const classes = useStyles()
  const { data, loading, refetch } = useQuery(V2_PRESENCE_QUERY, {
    variables: { deviceId },
    pollInterval: 15000
  })

  const presence = data?.flagshipDeviceV2?.presence
  const dcsOnline = useDcsOnlineWebsocket(deviceId, refetch)
  const isOnline = dcsOnline ?? presence?.online ?? false

  if (loading && !presence) return null

  const rows = [
    {
      key: 'Online',
      value: isOnline ? (
        <Chip label='Online' size='small' className={classes.chip} style={{ backgroundColor: '#4caf50', color: '#fff' }} />
      ) : (
        <Chip label='Offline' size='small' className={classes.chip} style={{ backgroundColor: '#f44336', color: '#fff' }} />
      )
    },
    {
      key: 'Transport',
      value: presence?.transport || '—'
    },
    {
      key: 'SSID',
      value: presence?.ssid || '—'
    },
    {
      key: 'IP Address',
      value: presence?.ipAddress || '—'
    },
    {
      key: 'Wifi IP',
      value: presence?.wifiIpAddress || '—'
    },
    {
      key: 'Ethernet',
      value: presence?.ethernetOnline ? (
        <span>Online — {presence?.ethernetIpAddress || 'no IP'}</span>
      ) : (
        'Offline'
      )
    },
    {
      key: 'Mask Status',
      value: presence?.maskStatus || '—'
    },
    {
      key: 'Firmware Version',
      value: presence?.firmwareVersion || '—'
    },
    ...(presence?.firmwareUpgradeStatus && presence.firmwareUpgradeStatus !== 'idle'
      ? [
          {
            key: 'Firmware Upgrade',
            value: `${presence.firmwareUpgradeStatus} (${presence.firmwareUpgradeProgress || 0}%)`
          }
        ]
      : []),
    {
      key: 'Connected At',
      value: presence?.connectedAt ? moment(presence.connectedAt).format('YYYY-MM-DD HH:mm:ss') : '—'
    },
    {
      key: 'Last Contact',
      value: presence?.lastContact ? moment(presence.lastContact).format('YYYY-MM-DD HH:mm:ss') : '—'
    },
    {
      key: 'Last Seen',
      value: presence?.lastSeen ? (
        <span>
          {moment(presence.lastSeen).fromNow()} ({moment(presence.lastSeen).format('YYYY-MM-DD HH:mm:ss')})
        </span>
      ) : (
        '—'
      )
    }
  ]

  return (
    <Card className={classes.card}>
      <Typography variant='h6' className={classes.title}>
        V2 Presence (DCS)
        {isOnline ? (
          <Chip label='LIVE' size='small' style={{ backgroundColor: '#4caf50', color: '#fff', fontWeight: 700 }} />
        ) : null}
        {dcsOnline !== null ? (
          <Chip label='WS' size='small' style={{ backgroundColor: '#2196f3', color: '#fff', fontWeight: 700 }} />
        ) : null}
      </Typography>
      {!presence ? (
        <Typography variant='body2' color='textSecondary'>
          No V2 presence data available for this device.
        </Typography>
      ) : (
        <Table size='small'>
          <TableBody>
            {rows.map(row => (
              <TableRow key={row.key}>
                <TableCell style={{ fontWeight: 600, width: 180 }}>{row.key}</TableCell>
                <TableCell>{row.value}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Card>
  )
}
